import React, { Suspense } from 'react';
import { initReactI18next } from 'react-i18next';
import environment from '@ktag/styleguide/src/javascripts/environment/environment';
import {
  WebWrapper,
  PageWrapper,
  Header,
  Footer,
  Pagesection,
} from '@ktag/styleguide/dist/react';
import analytics from '@ktag/styleguide/src/javascripts/analytics/analytics';
import { i18n } from '@ktag/styleguide/dist/base';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppContextProvider, AppMode, ActionsOrigin } from 'AppContext';
import { NewsletterConfig } from 'types/config.types';
import Stylesheet from 'components/stylesheet/Stylesheet';
import environmentConfig from './config/environment';
import Newsletter from './components/newsletter/Newsletter';
import 'app.scss';

const queryClient = new QueryClient({
  defaultOptions: {
    // queries: {
    //   retry: false,
    //   cacheTime: Infinity,
    //   refetchInterval: false,
    //   staleTime: Infinity,
    // },
  },
});

interface Props {
  rootElement?: HTMLElement;
  standalone?: boolean;
  mode?: string;
  config?: NewsletterConfig;
  apiUrl?: string;
  translationApiUrl?: string | null;
  translationMissingApiUrl?: string | null;
  manageBaseUrl?: string;
  overviewBaseUrl?: string;
  viewerBaseUrl?: string;
  latestId?: number;
  latestCount?: number;
  actionsShowSubscribe?: boolean;
  actionsShowEdit?: boolean;
  actionsShowUnsubscribe?: boolean;
  actionsId?: number;
  actionsOrigin?: ActionsOrigin;
  siteKey?: string;
}

interface I18nProjectConfig {
  ns?: string[];
  backend?: { loadPath: string; addPath?: string };
  saveMissing?: boolean;
}

function App(props: Props) {
  const {
    rootElement,
    standalone,
    mode,
    config,
    apiUrl,
    translationApiUrl,
    translationMissingApiUrl,
    manageBaseUrl,
    overviewBaseUrl,
    viewerBaseUrl,
    latestId,
    latestCount,
    actionsShowSubscribe,
    actionsShowEdit,
    actionsShowUnsubscribe,
    actionsId,
    actionsOrigin,
    siteKey,
  } = props;

  // Initialize the environment/analytics with the web-config from @ktag/styleguide
  // if you do NOT pass an environmentConfig, the app will try to use the environment
  // config of the 'surrounding' environment, if it is e.g. embedded in www.ag.ch
  environment.initialize(environmentConfig);
  analytics.initialize();

  // initialize i18n and declare all known required namespaces
  // upfront to combine loading labels into a single request.
  const i18nProjectConfig: I18nProjectConfig = {
    ns: ['core', 'newsletter'],
  };
  if (translationApiUrl) {
    i18nProjectConfig.backend = { loadPath: translationApiUrl };
    if (translationMissingApiUrl) {
      i18nProjectConfig.saveMissing = true;
      i18nProjectConfig.backend.addPath = translationMissingApiUrl;
    }
  }
  i18n.initialize(i18nProjectConfig, {
    plugin: initReactI18next,
    allowMultiLoading: true,
  });

  // Production view (not standalone)
  // Nothing but the actual app is rendered. This will also make the app
  // dependent on CSS and JS from the surrounding web page. Upside is that
  // the look of your app will update with the surrounding page. Downside is
  // that this may break your app. If you want to isolate from outside CSS and
  // JS then provide your own WebWrapper and Stylesheet (see standalone view)
  // and set the app integration to be 'isolated'.
  if (standalone !== true) {
    return (
      <Suspense fallback={<div />}>
        <WebWrapper renderDocument resetCSS parentElement={rootElement}>
          <QueryClientProvider client={queryClient}>
            <AppContextProvider
              baseApiUrl={apiUrl}
              config={config}
              manageBaseUrl={manageBaseUrl}
              overviewBaseUrl={overviewBaseUrl}
              viewerBaseUrl={viewerBaseUrl}
              latestId={latestId}
              latestCount={latestCount}
              actionsShowSubscribe={actionsShowSubscribe}
              actionsShowEdit={actionsShowEdit}
              actionsShowUnsubscribe={actionsShowUnsubscribe}
              actionsId={actionsId}
              actionsOrigin={actionsOrigin}
              siteKey={siteKey}
            >
              <Newsletter mode={mode} />
            </AppContextProvider>
          </QueryClientProvider>
        </WebWrapper>
      </Suspense>
    );
  }

  // Production view (standalone)
  // A complete web view is rendered around the app including
  // the loading of all necessary resources (css, images, fonts, icons, ...)
  return (
    <Suspense fallback={<div />}>
      <WebWrapper renderDocument resetCSS parentElement={rootElement}>
        <Stylesheet inShadowDOM />
        <PageWrapper>
          <Header />
          <PageWrapper.Main>
            <Pagesection type="slender">
              <div className="richtext">
                <Suspense fallback={<div />}>
                  <QueryClientProvider client={queryClient}>
                    <AppContextProvider
                      baseApiUrl={apiUrl}
                      config={config}
                      manageBaseUrl={manageBaseUrl}
                      overviewBaseUrl={overviewBaseUrl}
                      viewerBaseUrl={viewerBaseUrl}
                      latestId={latestId}
                      latestCount={latestCount}
                      actionsShowSubscribe={actionsShowSubscribe}
                      actionsShowEdit={actionsShowEdit}
                      actionsShowUnsubscribe={actionsShowUnsubscribe}
                      actionsId={actionsId}
                      actionsOrigin={actionsOrigin}
                      siteKey={siteKey}
                    >
                      <Newsletter mode={mode} />
                    </AppContextProvider>
                  </QueryClientProvider>
                </Suspense>
              </div>
            </Pagesection>
          </PageWrapper.Main>
          <Footer />
        </PageWrapper>
      </WebWrapper>
    </Suspense>
  );
}

App.defaultProps = {
  standalone: false,
  apiUrl: '',
  translationApiUrl: null,
  translationMissingApiUrl: null,
  mode: 'list' as AppMode,
};

export default App;
